import { IconName, Theme } from '@infotech-soe/erp-ui-kit';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

const messages = defineMessages({
  expand: {
    id: 'core.form.expand',
    defaultMessage: 'Expand',
  },
  collapse: {
    id: 'core.form.collapse',
    defaultMessage: 'Collapse',
  },
});

const useStyles = createUseStyles(({ colors }: Theme) => ({
  '@global': {
    '[role="tooltip"]': {
      zIndex: 10000,
    },
  },
  expanded: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    padding: 8,
    backgroundColor: colors.surface,
    '& > label': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    '& > label > div:first-child': {
      flex: '0 0 auto',
    },
    '& > label > div:last-child': {
      flex: '1 1 0',
      minHeight: 0,
    },
    '& > label > div:last-child > span': {
      height: '100%',
    },
    '& > label > div:last-child > span > div': {
      height: '100%',
    },
    '& textarea': {
      height: '100% !important',
    },
  },
}));

interface UseExpandableOptions {
  enabled?: boolean;
}

interface UseExpandableReturn {
  isExpanded: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  toggleExpanded: () => void;
  buttonIcon: IconName;
  buttonTitle: string;
  renderExpanded: (children: React.ReactNode) => React.ReactPortal;
}

const useExpandable = ({
  enabled = false,
}: UseExpandableOptions = {}): UseExpandableReturn => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldRestoreFocusRef = useRef(false);
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const toggleExpanded = useCallback(() => {
    setIsExpanded(expanded => {
      if (!expanded) {
        shouldRestoreFocusRef.current = true;
      }
      return !expanded;
    });
  }, []);

  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isExpanded]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F11' && containerRef.current?.contains(e.target as Node)) {
        e.preventDefault();
        toggleExpanded();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [enabled, toggleExpanded]);

  useLayoutEffect(() => {
    if (isExpanded || !shouldRestoreFocusRef.current) return;

    containerRef.current
      ?.querySelector<HTMLTextAreaElement>('textarea')
      ?.focus();
    shouldRestoreFocusRef.current = false;
  }, [isExpanded]);

  const renderExpanded = (children: React.ReactNode): React.ReactPortal =>
    ReactDOM.createPortal(
      <div ref={containerRef} className={classes.expanded}>
        {children}
      </div>,
      document.body
    );

  return {
    isExpanded,
    containerRef,
    toggleExpanded,
    buttonIcon: (isExpanded ? 'arrow-collapse' : 'arrow-expand') as IconName,
    buttonTitle: formatMessage(
      isExpanded ? messages.collapse : messages.expand
    ),
    renderExpanded,
  };
};

export default useExpandable;
